export function isTempEdgeSuccess(response) {
  if (!response || typeof response !== 'object' || response instanceof Error) {
    return false;
  }

  if (response.code !== 'TE00') return false;

  const status = Number(response.status);
  return Number.isNaN(status) || status < 300;
}

const asArray = (value) => (Array.isArray(value) ? value : []);

export function normalizeReportResponse(rawResponse = {}) {
  return {
    ...rawResponse,
    employeesCount: rawResponse.employeesCount ?? 0,
    missName: rawResponse.missName ?? 0,
    noEmployeeNoList: asArray(rawResponse.noEmployeeNoList),
    missPunchList: asArray(rawResponse.missPunchList),
    notConfiguredEmployeeList: asArray(rawResponse.notConfiguredEmployeeList),
    recurrentPositionNotFoundList: asArray(rawResponse.recurrentPositionNotFoundList),
    payrollEntityList: asArray(rawResponse.payrollEntityList)
  };
}

export function resolveSaveOutcome(responseOrError, { onSuccess, onError } = {}) {
  const apiData = responseOrError?.response?.data ?? responseOrError;

  if (isTempEdgeSuccess(apiData)) {
    onSuccess?.(apiData);
    return true;
  }

  const fallbackMessage = responseOrError?.message || GENERIC_ERROR_MESSAGE_KEY;
  const errorPayload = apiData || new Error(fallbackMessage);
  onError?.(errorPayload);
  return false;
}

export function getTempEdgeResponseCode(response) {
  if (!response || response instanceof Error) return undefined;
  return response.code ?? response?.data?.code;
}

export function getTempEdgeResponseStatus(response) {
  if (!response || response instanceof Error) return undefined;
  return response.status ?? response?.data?.status;
}

/**
 * Payload the screens hand to OutcomeBar to report a successful operation. Single
 * source of truth so the success bar reads the same everywhere.
 */
export const SUCCESS_MESSAGE = Object.freeze({
  status: 200,
  code: 'TE00',
  message: 'com.tempedge.msg.info.msg.success',
  showCode: false
});

export const GENERIC_ERROR_MESSAGE_KEY = 'com.tempedge.error.undefine';

const isTranslationKey = (value) => typeof value === 'string' && value.startsWith('com.tempedge.');

/**
 * Normalizes anything a failed call can produce — an axios error, a bare Error, or a
 * TempEdge response that did not succeed — into a payload OutcomeBar can render.
 *
 * OutcomeBar resolves `response.message` against the dictionary and falls back to
 * `com.tempedge.error.undefine` when it is not a known key, so handing it a raw Error
 * turns every failure into the same generic bar. Passing the call site's own key keeps
 * the message specific to what actually failed.
 * @param {*} responseOrError - Rejected value or unsuccessful response.
 * @param {string} fallbackMessageKey - Translation key describing the failed operation.
 * @returns {{status: number, code: string, message: string, showCode: boolean}|Object} Renderable payload.
 * @author armandoricosandoval -ai-
 */
export function toOutcomeBarFailure(responseOrError, fallbackMessageKey) {
  const apiData = responseOrError?.response?.data ?? responseOrError;

  if (isTranslationKey(apiData?.message)) return apiData;

  const isNetworkFailure = responseOrError instanceof Error && !responseOrError?.response;
  const status = Number(apiData?.status ?? responseOrError?.response?.status);

  return {
    status: Number.isFinite(status) && status > 0 ? status : 400,
    code: getTempEdgeResponseCode(apiData) ?? 'TE-E08',
    message: isNetworkFailure ? 'com.tempedge.msg.error.networkerror' : fallbackMessageKey,
    showCode: false
  };
}

export function isHookMessageForOutcomeBar(message) {
  if (!message || message instanceof Error) return false;
  if (isTempEdgeSuccess(message)) return true;
  return message.code != null || message.status != null;
}
